/**
 * Image Cache Table Component
 *
 * Displays cached workspace images with status and delete actions.
 */

import { type ReactElement } from 'react';
import { useAdminImageCache, useDeleteImageCache } from '../../hooks/useAdminImageCache';

const STATUS_CLASSES: Record<string, string> = {
  ready: 'feature-table__toggle--active',
  building: '',
  failed: 'feature-table__badge',
};

export function ImageCacheTable(): ReactElement {
  const { data: images, isLoading } = useAdminImageCache();
  const deleteMutation = useDeleteImageCache();

  if (isLoading) {
    return <p>Loading image cache...</p>;
  }

  if (!images || images.length === 0) {
    return <p>No cached images.</p>;
  }

  return (
    <div className="feature-table">
      <table className="feature-table__table">
        <thead>
          <tr>
            <th>Status</th>
            <th>Feature Hash</th>
            <th>Features</th>
            <th>Created</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {images.map((entry) => (
            <tr key={entry.id}>
              <td>
                <span className={STATUS_CLASSES[entry.status] ?? ''}>
                  {entry.status}
                </span>
              </td>
              <td className="feature-table__source">
                {entry.feature_hash.slice(0, 12)}
              </td>
              <td>{Object.keys(entry.features_json).join(', ') || '-'}</td>
              <td>{new Date(entry.created_at).toLocaleString()}</td>
              <td>
                <button
                  type="button"
                  className="feature-table__edit-btn"
                  onClick={() => { deleteMutation.mutate(entry.id); }}
                  disabled={deleteMutation.isPending}
                  aria-label={`Delete cache entry ${entry.feature_hash.slice(0, 12)}`}
                >
                  Delete
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
